import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { FilterBar } from '@/components/ui/FilterBar';
import { Modal } from '@/components/ui/Modal';
import { Input, Select, Textarea } from '@/components/ui/Input';
import { ProductPicker } from '@/components/ui/ProductPicker';
import { api } from '@/api/client';
import { useAuth } from '@/context/AuthContext';
import { useCurrency, formatCurrency } from '@/hooks/useCurrency';
import { BUILD_STATUSES, RMA_STATUSES } from '@/utils/helpers';
import { Plus, Edit, Trash, Wrench, RefreshCw, ArrowRightCircle, History, Cpu } from 'lucide-react';

const statusVariant = (s) => {
  switch (s) {
    case 'Building': return 'info';
    case 'Testing': return 'warning';
    case 'Ready for Delivery': return 'success';
    case 'Delivered': return 'default';
    case 'Cancelled': return 'danger';
    default: return 'default';
  }
};

const computeBuildTotal = (b) => {
  const sub = (b.components || []).reduce((s, c) => s + (c.qty || 1) * (c.price || 0), 0);
  return sub + (b.labor_charge || 0);
};

const emptyBuild = () => ({
  build_name: '',
  customer_id: '',
  customer_name: '',
  components: [],
  labor_charge: 0,
  notes: '',
  test_notes: '',
  status: 'Building',
  assigned_technician_name: '',
  tested_by_name: ''
});

export const Builds = () => {
  const currency = useCurrency();
  const { isAdmin, isSuperAdmin, user } = useAuth();
  const [builds, setBuilds] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [swapState, setSwapState] = useState(null); // { build, line }
  const [historyBuild, setHistoryBuild] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [b, c] = await Promise.all([api.getBuilds(), api.getCustomers()]);
      setBuilds(b.data || []);
      setCustomers(c.data || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return builds.filter((b) => {
      if (statusFilter && b.status !== statusFilter) return false;
      if (!q) return true;
      const hay = `${b.build_number || ''} ${b.build_name || ''} ${b.customer_name || ''} ${(b.components || []).map(c => `${c.product_name} ${c.serial_current}`).join(' ')}`.toLowerCase();
      return hay.includes(q);
    });
  }, [builds, search, statusFilter]);

  const handleDelete = async (b) => {
    if (!window.confirm(`Delete ${b.build_number}? Reserved serials will be released back to stock.`)) return;
    try {
      await api.deleteBuild(b.id);
      loadAll();
    } catch (e) { alert(e?.response?.data?.detail || 'Failed to delete'); }
  };

  const handleConvert = async (b) => {
    if (!b.customer_id) {
      alert('Set a customer on this build first, then try again.');
      return;
    }
    if (!window.confirm(`Convert ${b.build_number} into an Invoice for ${b.customer_name || 'customer'}? Total: ${formatCurrency(computeBuildTotal(b), currency)}`)) return;
    try {
      const res = await api.convertBuildToInvoice(b.id);
      alert(`Invoice created. Total: ${formatCurrency(res.data.total, currency)}`);
      loadAll();
    } catch (e) { alert(e?.response?.data?.detail || 'Failed to convert'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">PC Builds</h1>
          <p className="text-sm text-[var(--text-muted)]">Track assembly, testing, and delivery — with full component-swap history.</p>
        </div>
        <Button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="new-build-btn">
          <Plus size={16} className="mr-1" /> New Build
        </Button>
      </div>

      <Card>
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by build #, name, customer, component, serial..."
          filters={[{ key: 'status', label: 'All Statuses', value: statusFilter, options: BUILD_STATUSES, onChange: setStatusFilter }]}
          onClear={() => { setSearch(''); setStatusFilter(''); }}
          testId="builds-filter"
        />
      </Card>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Build #</TableHead>
              <TableHead>Build Name</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Components</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Technician</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">No builds yet — click &quot;New Build&quot; to start.</TableCell></TableRow>
            ) : filtered.map((b) => (
              <TableRow key={b.id}>
                <TableCell className="font-mono text-xs font-semibold">{b.build_number}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Cpu size={14} className="text-[var(--brand-cyan)]" />
                    <span>{b.build_name}</span>
                  </div>
                </TableCell>
                <TableCell>{b.customer_name || <span className="text-gray-400 italic text-xs">— in-house —</span>}</TableCell>
                <TableCell>
                  <span className="text-xs text-[var(--text-muted)]">{(b.components || []).length} parts</span>
                </TableCell>
                <TableCell className="font-semibold">{formatCurrency(computeBuildTotal(b), currency)}</TableCell>
                <TableCell className="text-xs">{b.assigned_technician_name || '-'}</TableCell>
                <TableCell><Badge variant={statusVariant(b.status)}>{b.status}</Badge></TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(b); setShowForm(true); }} title="Edit" data-testid={`edit-build-${b.id}`}><Edit size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setSwapState({ build: b })} title="Swap component (DOA)" data-testid={`swap-build-${b.id}`}><RefreshCw size={14} className="text-amber-600" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setHistoryBuild(b)} title="History" data-testid={`history-build-${b.id}`}><History size={14} /></Button>
                    {!b.invoice_id && (
                      <Button size="sm" variant="ghost" onClick={() => handleConvert(b)} title="Convert to invoice" data-testid={`convert-build-${b.id}`}><ArrowRightCircle size={14} className="text-emerald-600" /></Button>
                    )}
                    {isSuperAdmin() && !b.invoice_id && (
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(b)} title="Delete" data-testid={`delete-build-${b.id}`}><Trash size={14} className="text-red-600" /></Button>
                    )}
                    {b.invoice_id && <Badge variant="success" className="text-xs">Invoiced</Badge>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {showForm && (
        <BuildFormModal
          isOpen={showForm}
          onClose={() => setShowForm(false)}
          build={editing}
          customers={customers}
          user={user}
          onSaved={loadAll}
        />
      )}

      {swapState && (
        <SwapModal
          state={swapState}
          onClose={() => setSwapState(null)}
          onSaved={loadAll}
        />
      )}

      {historyBuild && (
        <HistoryModal build={historyBuild} onClose={() => setHistoryBuild(null)} />
      )}
    </div>
  );
};

// ===================== Build Form (Create / Edit) =====================
const BuildFormModal = ({ isOpen, onClose, build, customers, user, onSaved }) => {
  const [form, setForm] = useState(emptyBuild());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (build) {
      setForm({
        build_name: build.build_name || '',
        customer_id: build.customer_id || '',
        customer_name: build.customer_name || '',
        components: (build.components || []).map(c => ({ ...c })),
        labor_charge: build.labor_charge || 0,
        notes: build.notes || '',
        test_notes: build.test_notes || '',
        status: build.status || 'Building',
        assigned_technician_name: build.assigned_technician_name || '',
        tested_by_name: build.tested_by_name || ''
      });
    } else {
      setForm({ ...emptyBuild(), assigned_technician_name: user?.full_name || '' });
    }
  }, [build, user]);

  const handleAddComponent = (product, matchedSerial) => {
    if (!product) return;
    const comp = {
      line_id: (window.crypto?.randomUUID?.() || `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`),
      product_id: product.id,
      product_name: product.name,
      brand: product.brand || '',
      model: product.model || '',
      serial_current: matchedSerial || '',
      qty: 1,
      price: product.price || 0,
      cost: product.cost || 0
    };
    setForm((f) => ({ ...f, components: [...f.components, comp] }));
  };

  const updateLine = (line_id, patch) => {
    setForm((f) => ({ ...f, components: f.components.map(c => c.line_id === line_id ? { ...c, ...patch } : c) }));
  };

  const removeLine = (line_id) => {
    setForm((f) => ({ ...f, components: f.components.filter(c => c.line_id !== line_id) }));
  };

  const onCustomerChange = (id) => {
    const c = customers.find(x => x.id === id);
    setForm((f) => ({ ...f, customer_id: id, customer_name: c?.name || '' }));
  };

  const subTotal = form.components.reduce((s, c) => s + (c.price || 0) * (c.qty || 1), 0);
  const total = subTotal + (parseFloat(form.labor_charge) || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.build_name.trim()) { alert('Build name is required'); return; }
    if (form.components.length === 0) { alert('Add at least one component'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        labor_charge: parseFloat(form.labor_charge) || 0,
        components: form.components.map(c => ({
          ...c,
          qty: parseInt(c.qty) || 1,
          price: parseFloat(c.price) || 0,
          cost: parseFloat(c.cost) || 0,
        }))
      };
      if (build) await api.updateBuild(build.id, payload);
      else await api.createBuild(payload);
      onSaved();
      onClose();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save build');
    } finally { setSaving(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={build ? `Edit ${build.build_number}` : 'New PC Build'} size="xl">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Build Name *" value={form.build_name} onChange={(e) => setForm({ ...form, build_name: e.target.value })} placeholder="e.g. Gaming PC — i7 + RTX 5070" data-testid="build-name" />
          <Select label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="build-status">
            {BUILD_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </Select>
        </div>

        <Select label="Customer (optional — leave blank for in-house / spec build)" value={form.customer_id} onChange={(e) => onCustomerChange(e.target.value)} data-testid="build-customer">
          <option value="">— No customer (in-house) —</option>
          {customers.map(c => <option key={c.id} value={c.id}>{`${c.name} (${c.phone})`}</option>)}
        </Select>

        <div>
          <label className="block text-sm font-medium mb-2">Add Components</label>
          <ProductPicker onSelect={handleAddComponent} placeholder="Search component by name, brand, SKU, or serial..." testId="build-component-picker" />
        </div>

        {form.components.length > 0 && (
          <div className="border border-[var(--border)] rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Component</TableHead>
                  <TableHead>Serial</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {form.components.map((c) => (
                  <TableRow key={c.line_id}>
                    <TableCell>
                      <div className="font-medium text-sm">{c.product_name}</div>
                      <div className="text-xs text-[var(--text-muted)]">{c.brand} {c.model}</div>
                    </TableCell>
                    <TableCell>
                      <Input value={c.serial_current || ''} onChange={(e) => updateLine(c.line_id, { serial_current: e.target.value })} placeholder="serial" className="text-xs" data-testid={`build-line-serial-${c.line_id}`} />
                    </TableCell>
                    <TableCell><Input type="number" min="1" value={c.qty} onChange={(e) => updateLine(c.line_id, { qty: e.target.value })} className="w-16" /></TableCell>
                    <TableCell><Input type="number" step="0.01" value={c.price} onChange={(e) => updateLine(c.line_id, { price: e.target.value })} className="w-24" /></TableCell>
                    <TableCell><Button type="button" size="sm" variant="ghost" onClick={() => removeLine(c.line_id)}><Trash size={14} className="text-red-600" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Input label="Labor / Assembly Charge" type="number" step="0.01" value={form.labor_charge} onChange={(e) => setForm({ ...form, labor_charge: e.target.value })} data-testid="build-labor" />
          <Input label="Technician" value={form.assigned_technician_name} onChange={(e) => setForm({ ...form, assigned_technician_name: e.target.value })} placeholder="Who is assembling" />
        </div>

        <Input label="Tested By" value={form.tested_by_name} onChange={(e) => setForm({ ...form, tested_by_name: e.target.value })} placeholder="Filled when moving to Testing/Ready" />

        <Textarea label="Build Notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        <Textarea label="Testing Notes" rows={2} value={form.test_notes} onChange={(e) => setForm({ ...form, test_notes: e.target.value })} placeholder="Burn-in results, benchmarks, issues found" />

        <div className="flex justify-between items-center pt-3 border-t border-[var(--border)]">
          <div className="text-right">
            <div className="text-xs text-[var(--text-muted)]">Subtotal: {subTotal.toFixed(2)} + Labor: {(parseFloat(form.labor_charge) || 0).toFixed(2)}</div>
            <div className="text-lg font-bold">Total: {total.toFixed(2)}</div>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-build">{saving ? 'Saving...' : (build ? 'Update' : 'Create Build')}</Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

// ===================== Swap Component Modal =====================
const SwapModal = ({ state, onClose, onSaved }) => {
  const { build } = state;
  const [lineId, setLineId] = useState((build.components || [])[0]?.line_id || '');
  const [newSerial, setNewSerial] = useState('');
  const [reason, setReason] = useState('DOA during assembly');
  const [createRMA, setCreateRMA] = useState(true);
  const [rmaStatus, setRmaStatus] = useState('DOA');
  const [supplierName, setSupplierName] = useState('');
  const [saving, setSaving] = useState(false);

  const line = (build.components || []).find(c => c.line_id === lineId);

  const submit = async (e) => {
    e.preventDefault();
    if (!lineId) { alert('Select a component'); return; }
    if (!newSerial.trim() && !createRMA) { alert('Provide a new serial or enable "Create Internal RMA"'); return; }
    setSaving(true);
    try {
      await api.swapBuildComponent(build.id, {
        line_id: lineId,
        new_serial: newSerial.trim(),
        reason,
        create_rma: createRMA,
        rma_status: rmaStatus,
        supplier_name: supplierName
      });
      onSaved();
      onClose();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Swap failed');
    } finally { setSaving(false); }
  };

  return (
    <Modal isOpen onClose={onClose} title={`Swap Component — ${build.build_number}`} size="md">
      <form onSubmit={submit} className="space-y-3">
        <p className="text-xs text-[var(--text-muted)]">Use this when a component fails (DOA / testing) during assembly. The original serial is logged in the build history and an Internal RMA is automatically created for supplier claim.</p>
        <Select label="Faulty Component *" value={lineId} onChange={(e) => setLineId(e.target.value)} data-testid="swap-line">
          {(build.components || []).map(c => <option key={c.line_id} value={c.line_id}>{`${c.product_name}${c.serial_current ? ` — ${c.serial_current}` : ''}`}</option>)}
        </Select>
        {line && (
          <div className="bg-[var(--surface-hover)] p-2 rounded text-xs space-y-0.5">
            <div><span className="text-[var(--text-muted)]">Original SN:</span> <span className="font-mono">{line.serial_current || '(none)'}</span></div>
            <div><span className="text-[var(--text-muted)]">Product:</span> {line.product_name}</div>
          </div>
        )}
        <Input label="New Serial Number" value={newSerial} onChange={(e) => setNewSerial(e.target.value)} placeholder="Replacement serial (leave blank if not yet on hand)" data-testid="swap-new-serial" />
        <Textarea label="Reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="swap-reason" />

        <label htmlFor="create-rma" className="flex items-center gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded cursor-pointer">
          <input type="checkbox" id="create-rma" checked={createRMA} onChange={(e) => setCreateRMA(e.target.checked)} data-testid="swap-create-rma" />
          <span className="text-xs">Create Internal RMA for supplier claim</span>
        </label>

        {createRMA && (
          <div className="grid grid-cols-2 gap-3">
            <Select label="RMA Status" value={rmaStatus} onChange={(e) => setRmaStatus(e.target.value)} data-testid="swap-rma-status">
              {RMA_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </Select>
            <Input label="Supplier" value={supplierName} onChange={(e) => setSupplierName(e.target.value)} placeholder="e.g. Distributor name" data-testid="swap-supplier" />
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-[var(--border)]">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="save-swap"><Wrench size={14} className="mr-1" /> {saving ? 'Swapping...' : 'Confirm Swap'}</Button>
        </div>
      </form>
    </Modal>
  );
};

// ===================== History Modal =====================
const HistoryModal = ({ build, onClose }) => {
  const history = build.history || [];
  return (
    <Modal isOpen onClose={onClose} title={`History — ${build.build_number}`} size="lg">
      {history.length === 0 ? (
        <p className="text-sm text-[var(--text-muted)] py-6 text-center">No component swaps yet — this build has no DOA replacements logged.</p>
      ) : (
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          {history.map((h, idx) => (
            <div key={idx} className="border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-900/20 p-3 rounded">
              <div className="flex justify-between text-xs text-[var(--text-muted)] mb-1">
                <span>{h.timestamp ? new Date(h.timestamp).toLocaleString() : '-'}</span>
                <span>by {h.user_name}</span>
              </div>
              <div className="text-sm font-medium">{h.product_name}</div>
              <div className="text-xs mt-1">
                <span className="text-red-600">SN: <span className="font-mono">{h.original_serial || '(none)'}</span></span>
                <span className="mx-2">→</span>
                <span className="text-emerald-600">SN: <span className="font-mono">{h.new_serial || '(pending)'}</span></span>
              </div>
              <div className="text-xs text-[var(--text-muted)] mt-1 italic">{h.reason}</div>
              {h.rma_id && <Badge variant="warning" className="text-xs mt-1">Internal RMA created</Badge>}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};
